import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../../shared/components/select-menu/select-menu.component';
import { FreemiumService } from '../../../services/freemium.service';
import { UtilService } from '../../../services/util.service';
import { switchMap } from 'rxjs';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import {PosthogService} from '../../../services/posthog.service';
import {PosthogEventName} from '../../../services/types';
import {HubspotFormPhase} from '../../../services/hubspot/hubspot.types';

@Component({
  selector: 'app-trial-step-three',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectMenuComponent, AssetVersionPipe],
  templateUrl: './trial-step-three.component.html',
  styleUrl: './trial-step-three.component.scss',
})
export class TrialStepThreeComponent {
  private freemiumService = inject(FreemiumService);
  private destroyRef = inject(DestroyRef);
  private utilService = inject(UtilService);
  private posthogService = inject(PosthogService)

  @Input() companyNameControl!: FormControl<string | null>;
  @Input() jobTitleControl!: FormControl<string | null>;
  @Input() jobTitleItems: SelectItem<string>[] = [];
  @Input() firstName = '';
  @Output() nextStep = new EventEmitter<void>();

  protected readonly SelectMode = SelectMode;
  isLoading = false;

  private isFormValid(): boolean {
    return this.companyNameControl.valid && this.jobTitleControl.valid;
  }

  async onNext(): Promise<void> {
    if (!this.isFormValid()) {
      this.companyNameControl.markAsTouched();
      this.jobTitleControl.markAsTouched();
      return;
    }

    const companyName = this.companyNameControl.value || '';
    const jobTitle = this.jobTitleControl.value || '';

    this.isLoading = true;

    this.freemiumService.updateHubspotFields({company: companyName, jobtitle: jobTitle});
    const hubspotPayload = this.freemiumService.getHubspotFields();
    await this.freemiumService.sendToHubSpot(HubspotFormPhase.STEP_THREE, hubspotPayload, window.location.href)
      .catch(error => console.error('Error sending to HubSpot:', error));

    this.freemiumService.updateProfessionalDetails(companyName, jobTitle)
      .pipe(
        this.utilService.retryWithCondition(() => true),
        switchMap(() => this.freemiumService.completeSignup().pipe(
          this.utilService.retryWithCondition(() => true)
        )),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: async () => {
          this.isLoading = false;
          this.nextStep.emit();
          this.posthogService.capture(PosthogEventName.PROFILE_SUBMITTED, {'company_name': companyName, 'job_title': jobTitle})
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }
}
